import { useEffect, useState } from 'react'
import { BookOpen, ChevronLeft, ChevronRight, RotateCcw, X } from 'lucide-react'

const steps = [
  {
    target: 'caderno',
    title: 'Seu Caderno digital',
    description: 'Crie anotações, organize com tags, marque favoritas e anexe PDFs para manter todo o material de estudo em um só lugar.',
    icon: BookOpen
  },
  {
    target: 'revisao',
    title: 'Revisão no momento certo',
    description: 'Adicione um tema à revisão e o Agente de Estudo organiza sua fila para você recordar o conteúdo antes de esquecê-lo.',
    icon: RotateCcw
  }
]

const findVisibleTarget = target => Array.from(
  document.querySelectorAll(`[data-tour="${target}"]`)
).find(element => {
  const rect = element.getBoundingClientRect()
  const style = window.getComputedStyle(element)
  return rect.width > 0 && rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden'
})

function FeatureTour({ open, initialStep = 0, onClose }) {
  const [stepIndex, setStepIndex] = useState(initialStep)
  const [targetRect, setTargetRect] = useState(null)
  const step = steps[stepIndex]

  useEffect(() => {
    if (!open) return undefined

    setStepIndex(initialStep)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [open, initialStep])

  useEffect(() => {
    if (!open) return undefined

    const updatePosition = () => {
      const target = findVisibleTarget(step.target)
      if (!target) {
        setTargetRect(null)
        return
      }

      const rect = target.getBoundingClientRect()
      const padding = 6
      setTargetRect({
        top: Math.max(rect.top - padding, 6),
        left: Math.max(rect.left - padding, 6),
        right: Math.min(rect.right + padding, window.innerWidth - 6),
        bottom: Math.min(rect.bottom + padding, window.innerHeight - 6),
        width: Math.min(rect.width + padding * 2, window.innerWidth - 12),
        height: Math.min(rect.height + padding * 2, window.innerHeight - 12)
      })
    }

    const frame = window.requestAnimationFrame(updatePosition)
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)

    return () => {
      window.cancelAnimationFrame(frame)
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [open, step.target])

  useEffect(() => {
    if (!open) return undefined

    const handleEscape = event => {
      if (event.key === 'Escape') onClose()
    }
    const handleThemeChange = () => onClose()
    window.addEventListener('keydown', handleEscape)
    window.addEventListener('eduplex:theme-change', handleThemeChange)
    return () => {
      window.removeEventListener('keydown', handleEscape)
      window.removeEventListener('eduplex:theme-change', handleThemeChange)
    }
  }, [open, onClose])

  if (!open) return null

  const Icon = step.icon
  const tooltipWidth = Math.min(360, window.innerWidth - 24)
  const tooltipLeft = targetRect
    ? Math.min(
      Math.max(targetRect.left + targetRect.width / 2 - tooltipWidth / 2, 12),
      window.innerWidth - tooltipWidth - 12
    )
    : Math.max((window.innerWidth - tooltipWidth) / 2, 12)
  const targetIsLow = targetRect && targetRect.top > window.innerHeight / 2
  const tooltipPosition = targetRect
    ? targetIsLow
      ? { bottom: window.innerHeight - targetRect.top + 14 }
      : { top: targetRect.bottom + 14 }
    : { top: '50%', transform: 'translateY(-50%)' }

  const finishOrAdvance = () => {
    if (stepIndex === steps.length - 1) {
      onClose()
      return
    }
    setStepIndex(current => current + 1)
  }

  return (
    <div className="fixed inset-0 z-[100]" role="dialog" aria-modal="true" aria-labelledby="feature-tour-title">
      {targetRect ? (
        <div
          className="pointer-events-none fixed rounded-2xl ring-2 ring-white"
          style={{
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.68)'
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-black/70" />
      )}

      <section
        className="fixed z-10 rounded-2xl border border-gray-100 bg-white p-5 text-gray-900 shadow-2xl dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
        style={{ width: tooltipWidth, left: tooltipLeft, ...tooltipPosition }}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-black text-white dark:bg-white dark:text-black">
            <Icon size={19} />
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-black dark:hover:bg-zinc-800 dark:hover:text-white"
            aria-label="Fechar apresentação"
          >
            <X size={17} />
          </button>
        </div>

        <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-400">
          Passo {stepIndex + 1} de {steps.length}
        </p>
        <h2 id="feature-tour-title" className="text-lg font-semibold">{step.title}</h2>
        <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-zinc-300">{step.description}</p>

        <div className="mt-5 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setStepIndex(current => current - 1)}
            disabled={stepIndex === 0}
            className="inline-flex items-center gap-1 rounded-lg px-2 py-2 text-xs font-medium text-gray-500 hover:text-black disabled:invisible dark:hover:text-white"
          >
            <ChevronLeft size={15} /> Voltar
          </button>
          <button type="button" onClick={finishOrAdvance} className="btn-primary inline-flex items-center gap-1.5">
            {stepIndex === steps.length - 1 ? 'Concluir' : 'Próximo'}
            {stepIndex < steps.length - 1 && <ChevronRight size={15} />}
          </button>
        </div>
      </section>
    </div>
  )
}

export default FeatureTour
